/**
 * Boot splash (JS-двойник нативного стартового экрана).
 *
 * Нативный сплэш статичен: пока идёт чтение Keychain/refresh, пользователь
 * смотрит на неподвижную картинку. Поэтому нативный сплэш снимается сразу, как
 * только этот компонент смонтирован, и дальше на том же фоне живёт та же, но
 * уменьшенная плашка, по контуру которой бесконечно бежит синее свечение.
 *
 * Ограничения проекта: reanimated замокан в вебе и не используется, поэтому
 * только `Animated` из react-native + react-native-svg. `strokeDashoffset` не
 * transform и не opacity, так что нативный драйвер недоступен —
 * `useNativeDriver: false` здесь не недосмотр, а единственный рабочий режим.
 */
import React, { useEffect, useState } from 'react';
import {
  AccessibilityInfo,
  Animated,
  Easing,
  Image,
  Platform,
  StyleSheet,
  View,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import * as SplashScreen from 'expo-splash-screen';

/** Фон обязан совпадать с `backgroundColor` нативного сплэша в app.json, иначе на стыке мигнёт. */
const BACKGROUND = '#8E939B';
/** Сторона плашки; тот же размер, что `imageWidth` у конфиг-плагина expo-splash-screen. */
const PLATE = 150;
/** Скругление как у иконки приложения (~22.4% стороны). */
const PLATE_RADIUS = 34;
/** Зазор между краем плашки и осевой линией кольца + место под самый широкий ореол. */
const RING_GAP = 7;
/** Толщина самого широкого ореола: на неё же поджимаем путь внутрь бокса, чтобы его не срезало. */
const HALO_WIDTH = 12;

const BOX = PLATE + RING_GAP * 2; // 164: бокс SVG вокруг плашки
const RECT_INSET = HALO_WIDTH / 2;
const RECT_SIZE = BOX - HALO_WIDTH;
const RECT_RADIUS = PLATE_RADIUS + (RECT_SIZE - PLATE) / 2;

/**
 * Контур скруглённого квадрата, начиная с середины верхней стороны, по часовой.
 * Вынесено отдельной чистой функцией: это единственная часть компонента, которую
 * имеет смысл читать и проверять глазами отдельно от анимации.
 */
export function roundedSquarePath(inset: number, size: number, radius: number): string {
  const r = Math.max(0, Math.min(radius, size / 2));
  const left = inset;
  const top = inset;
  const right = inset + size;
  const bottom = inset + size;
  const midX = inset + size / 2;
  return [
    `M ${midX} ${top}`,
    `H ${right - r}`,
    `A ${r} ${r} 0 0 1 ${right} ${top + r}`,
    `V ${bottom - r}`,
    `A ${r} ${r} 0 0 1 ${right - r} ${bottom}`,
    `H ${left + r}`,
    `A ${r} ${r} 0 0 1 ${left} ${bottom - r}`,
    `V ${top + r}`,
    `A ${r} ${r} 0 0 1 ${left + r} ${top}`,
    'Z',
  ].join(' ');
}

/** Длина того же контура — нужна для strokeDasharray и амплитуды strokeDashoffset. */
export function roundedSquarePerimeter(size: number, radius: number): number {
  const r = Math.max(0, Math.min(radius, size / 2));
  return 4 * (size - 2 * r) + 2 * Math.PI * r;
}

const RING_PATH = roundedSquarePath(RECT_INSET, RECT_SIZE, RECT_RADIUS);
const RING_LENGTH = roundedSquarePerimeter(RECT_SIZE, RECT_RADIUS);
/** Длина светящегося сегмента — примерно пятая часть контура. */
const SEGMENT = Math.round(RING_LENGTH * 0.22);
const DASH = `${SEGMENT} ${RING_LENGTH - SEGMENT}`;
/** Один оборот сегмента по контуру. */
const SPIN_MS = 1600;

const AnimatedPath = Animated.createAnimatedComponent(Path);

const SPLASH_ICON = require('../../assets/splash-icon.png') as number;

export function BootSplash(): React.JSX.Element {
  const [progress] = useState(() => new Animated.Value(0));
  const [reduceMotion, setReduceMotion] = useState(false);

  // Нативный сплэш снимаем ровно здесь, а не по `ready`: иначе поверх этой
  // анимации всё время лежала бы статичная нативная картинка и смысла в ней не
  // было бы никакого. Фон у обоих экранов один (BACKGROUND), поэтому подмена
  // видна только по тому, что кольцо поехало. Кросс-фейд — на iOS, на Android
  // `fade` игнорируется, в вебе весь модуль no-op.
  useEffect(() => {
    SplashScreen.setOptions({ fade: true, duration: 250 });
    void SplashScreen.hideAsync().catch(() => {
      /* уже скрыт / не сконфигурирован — безобидно. */
    });
    if (Platform.OS === 'web') {
      // HTML-заглушка из index.html: снимаем её тем же кадром, что рисуем свою,
      // чтобы между ними не мелькнул фон документа.
      const hideHtml = (globalThis as { __vrmHideBootSplash?: () => void }).__vrmHideBootSplash;
      if (typeof hideHtml === 'function') hideHtml();
    }
  }, []);

  // Уважаем «уменьшить движение»: на системном уровне это просьба не крутить
  // бесконечные индикаторы. Кольцо тогда просто стоит на месте.
  useEffect(() => {
    let cancelled = false;
    void AccessibilityInfo.isReduceMotionEnabled()
      .then((enabled) => {
        if (!cancelled) setReduceMotion(Boolean(enabled));
      })
      .catch(() => {
        /* платформа не отдала настройку — считаем, что движение разрешено. */
      });
    const sub = AccessibilityInfo.addEventListener('reduceMotionChanged', (enabled: boolean) => {
      setReduceMotion(Boolean(enabled));
    });
    return () => {
      cancelled = true;
      // В react-native-web подписки может не быть вовсе (нет matchMedia).
      sub?.remove?.();
    };
  }, []);

  useEffect(() => {
    if (reduceMotion) {
      progress.setValue(0);
      return;
    }
    const loop = Animated.loop(
      Animated.timing(progress, {
        toValue: 1,
        duration: SPIN_MS,
        easing: Easing.linear,
        useNativeDriver: false,
        isInteraction: false,
      }),
    );
    loop.start();
    return () => {
      loop.stop();
      progress.setValue(0);
    };
  }, [progress, reduceMotion]);

  // Минус — чтобы штрих ехал по ходу пути (по часовой), а не против.
  const dashOffset = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -RING_LENGTH],
  });

  return (
    <View style={styles.root}>
      <View style={styles.plateWrap}>
        <Image source={SPLASH_ICON} style={styles.plate} resizeMode="contain" />
        <Svg
          width={BOX}
          height={BOX}
          viewBox={`0 0 ${BOX} ${BOX}`}
          style={styles.ring}
          pointerEvents="none"
        >
          {/* Дорожка: без неё контур «исчезает» там, где сегмента сейчас нет. */}
          <Path
            d={RING_PATH}
            stroke="#2563eb"
            strokeOpacity={0.16}
            strokeWidth={2}
            fill="none"
          />
          {/* Ореол набираем наложением полупрозрачных дуг: SVG-фильтры (FeGaussianBlur)
              на Android/старой архитектуре ведут себя непредсказуемо. */}
          <AnimatedPath
            d={RING_PATH}
            stroke="#3b82f6"
            strokeOpacity={0.18}
            strokeWidth={HALO_WIDTH}
            strokeLinecap="round"
            strokeDasharray={DASH}
            strokeDashoffset={dashOffset}
            fill="none"
          />
          <AnimatedPath
            d={RING_PATH}
            stroke="#60a5fa"
            strokeOpacity={0.4}
            strokeWidth={6}
            strokeLinecap="round"
            strokeDasharray={DASH}
            strokeDashoffset={dashOffset}
            fill="none"
          />
          <AnimatedPath
            d={RING_PATH}
            stroke="#60a5fa"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeDasharray={DASH}
            strokeDashoffset={dashOffset}
            fill="none"
          />
        </Svg>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: BACKGROUND,
  },
  plateWrap: {
    width: BOX,
    height: BOX,
    alignItems: 'center',
    justifyContent: 'center',
  },
  plate: {
    width: PLATE,
    height: PLATE,
    borderRadius: PLATE_RADIUS,
  },
  ring: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
});
